import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { ExecuteResultStats } from 'types';
import type { CommunicationCategoryData } from './types';
// Import Helpers
import { clearEmptyValuesFromObject, recursiveSumObject } from 'services/Helpers';
// import Components
import { Tag } from '@geneui/components';
import CommunicationCountInfoModal from './CommunicationCountInfoModal';
// Import Hooks
import useOnClickOutside from 'hooks/UseOnClickOutside';
// Import Enums
import {
    CommunicationTypeEnum,
    CommunicationStatusEnum,
    CommunicationReasonEnum,
    CommunicationReasonLabelEnum,
} from 'enums';
// Import Constants
import { CampaignLogStatusLabels, l } from 'constants/common';
// Import Styles
import styles from './communicationCountItem.module.scss';

interface Props {
    count: number;
    executionStatus: string;
    stats: Nullable<ExecuteResultStats>;
    segmentSize: Nullable<number>;
    segmentData: AnyObject;
    isPreCalculate: boolean;
    bonusAmount: Nullable<number>;
    currencyId: Nullable<string>;
}

const CommunicationCountItem: FC<Props> = (props) => {
    const { count, executionStatus, segmentSize, stats, segmentData, isPreCalculate, bonusAmount, currencyId } = props;
    const { t } = useTranslation();

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isCountInfoModalOpened, setIsCountInfoModalOpened] = useState(false);
    const popoverContentRef = useRef(null);
    const countBlockRef = useRef(null);

    useOnClickOutside(
        [popoverContentRef, countBlockRef],
        useCallback(() => {
            if (isPopoverOpen) {
                setIsPopoverOpen(false);
            }
        }, [isPopoverOpen]),
    );

    const skippedExecutionsCount = useMemo(() => {
        if (executionStatus !== CampaignLogStatusLabels[CampaignLogStatusLabels.Success]) {
            return 0;
        }
        return (segmentSize ?? count) - count;
    }, [count, segmentSize, executionStatus]);

    const isNonCustomerSegment = useMemo(() => {
        return segmentData?.TargetObject === 1;
    }, [segmentData]);

    const convertedStats: CommunicationCategoryData[] = useMemo(() => {
        const clearedStats = clearEmptyValuesFromObject({
            [CommunicationTypeEnum[CommunicationTypeEnum.Unknown]]: stats?.[CommunicationTypeEnum.Unknown],
            [CommunicationTypeEnum[CommunicationTypeEnum.Email]]: stats?.[CommunicationTypeEnum.Email],
            [CommunicationTypeEnum[CommunicationTypeEnum.SMS]]: stats?.[CommunicationTypeEnum.SMS],
            [CommunicationTypeEnum[CommunicationTypeEnum.PushNotification]]:
                stats?.[CommunicationTypeEnum.PushNotification],
            [CommunicationTypeEnum[CommunicationTypeEnum.PopUpNotification]]:
                stats?.[CommunicationTypeEnum.PopUpNotification],
            [CommunicationTypeEnum[CommunicationTypeEnum.InternalMessage]]:
                stats?.[CommunicationTypeEnum.InternalMessage],
            [CommunicationTypeEnum[CommunicationTypeEnum.Telegram]]: stats?.[CommunicationTypeEnum.Telegram],
            [CommunicationTypeEnum[CommunicationTypeEnum.CMSPopUpNotification]]:
                stats?.[CommunicationTypeEnum.CMSPopUpNotification],
            [CommunicationTypeEnum[CommunicationTypeEnum.Viber]]: stats?.[CommunicationTypeEnum.Viber],
            [CommunicationTypeEnum[CommunicationTypeEnum.WhatsApp]]: stats?.[CommunicationTypeEnum.WhatsApp],
            [CommunicationTypeEnum[CommunicationTypeEnum.WhatsAppProvider]]:
                stats?.[CommunicationTypeEnum.WhatsAppProvider],
            [CommunicationTypeEnum[CommunicationTypeEnum.CMSBanner]]: stats?.[CommunicationTypeEnum.CMSBanner],
            [CommunicationTypeEnum[CommunicationTypeEnum.Hoory]]: stats?.[CommunicationTypeEnum.Hoory],
            [CommunicationTypeEnum[CommunicationTypeEnum.DynamicPopUp]]: stats?.[CommunicationTypeEnum.DynamicPopUp],
            [CommunicationTypeEnum[CommunicationTypeEnum.Bonus]]: stats?.[CommunicationTypeEnum.Bonus],
        });

        const defaultStats = Object.entries(clearedStats).map(([title, values]) => {
            const newValues = clearEmptyValuesFromObject({
                [CommunicationStatusEnum[CommunicationStatusEnum.Succeeded]]: values[CommunicationStatusEnum.Succeeded],
                [CommunicationStatusEnum[CommunicationStatusEnum.Failed]]: values[CommunicationStatusEnum.Failed],
                [CommunicationStatusEnum[CommunicationStatusEnum.Pending]]: values[CommunicationStatusEnum.Pending],
                [CommunicationStatusEnum[CommunicationStatusEnum.Queued]]: values[CommunicationStatusEnum.Queued],
                [CommunicationStatusEnum[CommunicationStatusEnum.Skipped]]: isNonCustomerSegment
                    ? skippedExecutionsCount
                    : values[CommunicationStatusEnum.Skipped],
            });

            return {
                title,
                values: Object.entries(newValues).map(([status, countValues]) => ({
                    status,
                    count: recursiveSumObject(countValues),
                    values: Object.keys(countValues).map((key) => ({
                        label: t(l[CommunicationReasonLabelEnum[CommunicationReasonEnum[key]]]),
                        value: countValues[key],
                    })),
                })),
            };
        });

        if (!isPreCalculate) {
            return defaultStats;
        }

        const hasBonus = !!stats?.[CommunicationTypeEnum.Bonus];

        const foundBonusStat =
            defaultStats.find((stat) => stat.title === CommunicationTypeEnum[CommunicationTypeEnum.Bonus]) ?? null;

        const bonusAmountStatus: string = foundBonusStat?.values[0].status || t(l.Succeeded);

        return [
            {
                title: t(l.ExecutedSegmentCount),
                values: [
                    {
                        status: t(l.Succeeded),
                        count: segmentSize || 0,
                    },
                ],
            },
            ...defaultStats,
            ...(hasBonus
                ? [
                      {
                          title: t(l.BonusAmount),
                          values: [
                              {
                                  status: bonusAmountStatus,
                                  count: `${bonusAmount ?? 0} ${currencyId ?? ''}`.trim(),
                              },
                          ],
                      },
                  ]
                : []),
        ];
    }, [stats, isPreCalculate, t, segmentSize, bonusAmount, currencyId, isNonCustomerSegment, skippedExecutionsCount]);

    const calcCount = isNonCustomerSegment ? count + skippedExecutionsCount : count;

    return (
        <>
            <Tag
                name={`${t(l.Sent)} ${calcCount}`}
                onClick={() => !isNil(stats) && setIsCountInfoModalOpened(true)}
                cornerRadius="smooth-radius"
                appearance="outline"
                size="medium"
                className={classNames({ [styles.readOnlyTag]: isNil(stats) })}
            />
            {isCountInfoModalOpened && (
                <CommunicationCountInfoModal
                    onClose={() => setIsCountInfoModalOpened(false)}
                    convertedStats={convertedStats}
                />
            )}
        </>
    );
};

export default memo(CommunicationCountItem);
